import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    PENDING, IDLE, DATE_FORMAT,
    PAYMENT_FREE, PAYMENT_STRIPE, PAYMENT_WT, PAYMENT_OTHER,
    ACTIVE, INACTIVE,
} from "const"
import { MtAccountPackage } from "server/models"
import {
    useInput,
    PaymentOptionType,
    StateType,
} from "client/utils"
import {
    StoreType,
    AccountPackageValidation,
    SaveAccountPackage,
    closeModal,
    setAccountPackageEdit,
    setAccountPackageReloadList,
    setAccountPackageReloadEdit,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Feedback,
    DatePicker,
    SelectDropdown,
    SelectDropdownItem,
    Button,
    SpinnerIcon,
    Empty,
} from "client/components"

const paymentOptionArray = [PAYMENT_FREE, PAYMENT_STRIPE, PAYMENT_WT, PAYMENT_OTHER]

interface EditAccountPackageProps {
    accountPackage: MtAccountPackage
}

let isMounted: boolean

const Form = ({
    accountPackage,
}: EditAccountPackageProps) => {
    const accountView = useSelector((state: StoreType) => state.account.accountView)
    const packageAllowed = useSelector((state: StoreType) => state.accountPackage.packageAllowed)
    const packageList = useSelector((state: StoreType) => state.package.packageList)
    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [packageId, setPackageId] = useState(accountPackage.packageId)
    const [dateStart, setDateStart] = useState(accountPackage.dateStart)
    const [dateEnd, setDateEnd] = useState(accountPackage.dateEnd)
    const [paymentOption, setPaymentOption] = useState(accountPackage.paymentOption)
    const [stripePaymentId, setStripePaymentId] = useInput(accountPackage.stripePaymentId || "")
    const [additionalSilCalculations, setAdditionalSilCalculations] = useState(accountPackage.additionalSilCalculations)

    const [validation, setValidation] = useState<AccountPackageValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (!isMounted) return
        if (modals.length) dispatch(closeModal())
    }

    const onSave = (mode: number) => {
        if (dateStart > dateEnd) return
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const newAccountPackage: MtAccountPackage = {
            id: accountPackage.id,
            accountId: accountView.id,
            packageId,
            dateStart,
            dateEnd,
            paymentOption,
            stripePaymentId: paymentOption === PAYMENT_STRIPE && stripePaymentId.length ? stripePaymentId : null,
            usedSilCalculations: accountPackage.usedSilCalculations,
            additionalSilCalculations: additionalSilCalculations !== null ? additionalSilCalculations : 0,
        }

        SaveAccountPackage(newAccountPackage, lang)
        .then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)
            }
            if (res.validation) return

            dispatch(setAccountPackageEdit({
                ...newAccountPackage,
                id: res.newAccountPackageId || accountPackage.id,
            }))

            dispatch(setAccountPackageReloadList(false))
            setTimeout(() => {
                dispatch(setAccountPackageReloadList(true))
            }, 0)

            if (mode === 1) onClose()
            if (mode === 2) {
                dispatch(setAccountPackageReloadEdit(false))
                setTimeout(() => {
                    dispatch(setAccountPackageReloadEdit(true))
                }, 0)
            }
        })
    }

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.package}</Label>
                        <SelectDropdown title={packageList[packageId] ? packageList[packageId].name : <Empty />}>
                            {
                                packageAllowed.map((packId) => {
                                    const packg = packageList[packId]
                                    return (
                                        <SelectDropdownItem
                                            key={packId}
                                            onClick={() => setPackageId(packId)}
                                        >
                                            { packg.status === ACTIVE && packg.name }
                                            { packg.status === INACTIVE && `${packg.name} - ${lang[INACTIVE]}` }
                                        </SelectDropdownItem>
                                    )
                                })
                            }
                        </SelectDropdown>
                        <Feedback>{validation && validation.packageId}</Feedback>
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col xs={12} md={6}>
                    <FormGroup>
                        <Label>{lang.dateStart}</Label>
                        <DatePicker
                            selected={dateStart ? new Date(dateStart) : null}
                            onChange={(date: Date) => {
                                setDateStart(date)
                                if (date && dateEnd && date > dateEnd) {
                                    setValidation({
                                        ...validation,
                                        dateEnd: lang.dateEndLessDateStart,
                                    })
                                } else {
                                    setValidation({
                                        ...validation,
                                        dateEnd: "",
                                    })
                                }
                            }}
                            dateFormat={DATE_FORMAT}
                            isClearable
                        />
                        <Feedback>{validation && validation.dateStart}</Feedback>
                    </FormGroup>
                </Col>
                <Col xs={12} md={6}>
                    <FormGroup>
                        <Label>{lang.dateEnd}</Label>
                        <DatePicker
                            selected={dateEnd ? new Date(dateEnd) : null}
                            onChange={(date: Date) => {
                                setDateEnd(date)
                                if (dateStart && date && dateStart > date) {
                                    setValidation({
                                        ...validation,
                                        dateEnd: lang.dateEndLessDateStart,
                                    })
                                } else {
                                    setValidation({
                                        ...validation,
                                        dateEnd: "",
                                    })
                                }
                            }}
                            dateFormat={DATE_FORMAT}
                            isClearable
                        />
                        <Feedback>{validation && validation.dateEnd}</Feedback>
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.paymentMethod}</Label>
                        <SelectDropdown title={paymentOption ? lang[paymentOption] : <Empty />}>
                            {
                                paymentOptionArray.map((paymentOpt: PaymentOptionType) => (
                                    <SelectDropdownItem
                                        key={paymentOpt}
                                        onClick={() => setPaymentOption(paymentOpt)}
                                    >
                                        {lang[paymentOpt]}
                                    </SelectDropdownItem>
                                ))
                            }
                        </SelectDropdown>
                        <Feedback>{validation && validation.paymentOption}</Feedback>
                    </FormGroup>
                    {
                        paymentOption === PAYMENT_STRIPE && (
                            <FormGroup>
                                <Label>{lang.stripeOrder}</Label>
                                <Input type="text" value={stripePaymentId} onChange={setStripePaymentId} />
                                <Feedback>{validation && validation.stripePaymentId}</Feedback>
                            </FormGroup>
                        )
                    }
                    <FormGroup>
                        <Label>{lang.additionalCalculations}</Label>
                        <Input
                            type="text"
                            value={additionalSilCalculations !== null ? additionalSilCalculations : ""}
                            onChange={(e) => {
                                const parsed = parseInt(e.target.value, 10)
                                if (!Number.isNaN(parsed)) {
                                    setAdditionalSilCalculations(parsed)
                                } else {
                                    setAdditionalSilCalculations(null)
                                }
                            }}
                        />
                        <Feedback>{validation && validation.additionalSilCalculations}</Feedback>
                    </FormGroup>
                </Col>

                <Col xs={12}>
                    <Button variant="success" row onClick={() => onSave(1)}>
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button variant="success" row onClick={() => onSave(2)}>
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
